import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import {
  deleteHousingUnit,
  listHousingUnits,
  updateHousingUnit,
} from "../lib/api-modules/housing-units.js";
import {
  OWNERSHIP_TYPE_LABELS,
  type HousingUnit,
  type MembershipRole,
} from "../lib/api-modules/types.js";
import { formatCentsCurrency } from "../lib/util/money.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { HousingUnitForm } from "../components/housing/HousingUnitForm.js";
import { HousingUnitDrilldown } from "../components/housing/HousingUnitDrilldown.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

/**
 * /housing — household-scoped real-estate management. Lists each unit
 * as a card; clicking a card expands its multi-tab drilldown
 * (Overview / Expenses / Assessments / Amortization / Refinance).
 *
 * Co-op-aware: Andre's NYC co-op is the v1 test case. The form's
 * conditional sections handle shares + flip tax + underlying mortgage
 * specific to co-ops. NYC Façade Law 11 / FISP gets typed enum support
 * on assessments.
 */

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

export function HousingPage() {
  const { user } = useAuth();
  if (!user) return null;
  const canWrite = !READ_ONLY_ROLES.has(user.role);

  return (
    <div className="mx-auto max-w-5xl p-6">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Housing</h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            Real estate, expenses + deductibility, NYC Façade Law 11
            assessments, and mortgage / refinance modeling.
          </p>
        </div>
      </header>

      {!canWrite && (
        <p className="mb-6 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          Read-only access — ask an owner or admin to make changes.
        </p>
      )}

      <HousingUnitsList canWrite={canWrite} />
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.housing} />
      </div>
    </div>
  );
}

function HousingUnitsList({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const { data, error, isLoading } = useQuery({
    queryKey: ["housing-units", { includeArchived: showArchived }],
    queryFn: () => listHousingUnits(showArchived),
  });

  const archiveMutation = useMutation({
    mutationFn: (unitId: string) => deleteHousingUnit(unitId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["housing-units"] });
      toast.show("Unit archived");
      setSelectedUnitId(null);
    },
    onError: (err) => {
      toast.show(
        err instanceof ApiError
          ? `Couldn't archive: ${err.message}`
          : "Couldn't archive unit",
      );
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (unitId: string) =>
      updateHousingUnit(unitId, { is_archived: false }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["housing-units"] });
      toast.show("Unit restored");
    },
    onError: () => toast.show("Couldn't restore unit"),
  });

  if (isLoading) {
    return (
      <p className="text-sm text-zinc-500" role="status">
        Loading housing units…
      </p>
    );
  }
  if (error) {
    return (
      <ErrorMessage>
        {error instanceof ApiError ? error.message : "Couldn't load housing."}
      </ErrorMessage>
    );
  }

  const units = data?.housing_units ?? [];

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-zinc-600 dark:text-zinc-400">
          {units.length} {units.length === 1 ? "unit" : "units"}
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-400">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
          {canWrite && !showCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600"
            >
              Add unit
            </button>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="mb-4">
          <HousingUnitForm
            mode="create"
            onClose={() => setShowCreate(false)}
          />
        </div>
      )}

      {units.length === 0 && !showCreate && (
        <p className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
          No housing units yet.{" "}
          {canWrite ? "Click \"Add unit\" to start." : ""}
        </p>
      )}

      <ul className="flex flex-col gap-3">
        {units.map((u) => (
          <li key={u.unit_id}>
            {editingId === u.unit_id ? (
              <div className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
                <HousingUnitForm
                  mode="edit"
                  unit={u}
                  onClose={() => setEditingId(null)}
                />
              </div>
            ) : (
              <article className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
                <HousingUnitCard
                  unit={u}
                  selected={selectedUnitId === u.unit_id}
                  canWrite={canWrite}
                  onSelect={() =>
                    setSelectedUnitId((id) =>
                      id === u.unit_id ? null : u.unit_id,
                    )
                  }
                  onEdit={() => setEditingId(u.unit_id)}
                  onArchive={() => archiveMutation.mutate(u.unit_id)}
                  onRestore={() => restoreMutation.mutate(u.unit_id)}
                  isMutating={
                    archiveMutation.isPending || restoreMutation.isPending
                  }
                />
              </article>
            )}
            {selectedUnitId === u.unit_id && editingId !== u.unit_id && (
              <div className="mt-3">
                <HousingUnitDrilldown unit={u} canWrite={canWrite} />
              </div>
            )}
          </li>
        ))}
      </ul>

      <Toast message={toast.message} />
    </section>
  );
}

function HousingUnitCard({
  unit,
  selected,
  canWrite,
  onSelect,
  onEdit,
  onArchive,
  onRestore,
  isMutating,
}: {
  unit: HousingUnit;
  selected: boolean;
  canWrite: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onRestore: () => void;
  isMutating: boolean;
}) {
  const equity =
    unit.current_value_cents !== undefined &&
    unit.underlying_mortgage_share_cents !== undefined
      ? unit.current_value_cents - unit.underlying_mortgage_share_cents
      : null;

  return (
    <header className="flex flex-wrap items-start justify-between gap-3">
      <button
        type="button"
        onClick={onSelect}
        className="min-w-0 flex-1 text-left"
      >
        <h3 className="truncate text-base font-medium">
          {unit.address}
          {unit.unit_designation && `, ${unit.unit_designation}`}
          {unit.is_archived && (
            <span className="ml-2 rounded-full bg-zinc-200 px-2 py-0.5 text-xs font-normal text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              archived
            </span>
          )}
          {unit.is_primary_residence && (
            <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-normal text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
              primary
            </span>
          )}
          <span className="ml-2 rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-normal text-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
            {OWNERSHIP_TYPE_LABELS[unit.ownership_type]}
          </span>
        </h3>
        <div className="mt-1 text-xs text-zinc-500">
          {unit.city}, {unit.state} {unit.zip}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {unit.current_value_cents !== undefined && (
            <span>
              Value:{" "}
              <span className="font-mono">
                {formatCentsCurrency(unit.current_value_cents)}
              </span>
            </span>
          )}
          {equity !== null && (
            <span>
              Equity:{" "}
              <span className="font-mono">{formatCentsCurrency(equity)}</span>
            </span>
          )}
          {unit.shares_held !== undefined && (
            <span>
              Shares: <span className="font-mono">{unit.shares_held}</span>
            </span>
          )}
        </div>
      </button>
      {canWrite && (
        <div className="flex shrink-0 items-center gap-1.5">
          {!unit.is_archived ? (
            <>
              <button
                type="button"
                onClick={onEdit}
                disabled={isMutating}
                className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 disabled:opacity-60 dark:border-zinc-700 dark:hover:bg-zinc-900"
              >
                Edit
              </button>
              <ConfirmButton
                label="Archive"
                pendingLabel="Archiving…"
                size="xs"
                onConfirm={onArchive}
                isPending={isMutating}
              />
            </>
          ) : (
            <button
              type="button"
              onClick={onRestore}
              disabled={isMutating}
              className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 disabled:opacity-60 dark:border-zinc-700 dark:hover:bg-zinc-900"
            >
              Restore
            </button>
          )}
          <button
            type="button"
            onClick={onSelect}
            className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
          >
            {selected ? "Hide" : "Open"}
          </button>
        </div>
      )}
    </header>
  );
}
